import * as _ from 'lodash';
import * as React from 'react';
import {
  ColumnsConfig,
  ColumnsInfo,
  FilterValueType,
  ROW_CHILDREN_INDICES,
  ROW_INDEX,
  ROW_PARENT_INDEX,
  RowDataType,
} from './redux/actions';
import InlineButton, {hoverContainerStyles} from '../../shared/components/InlineButton';
import {ClassNameMap} from '@material-ui/core/styles/withStyles';
import {EditableTableCellProps} from './EditableTableCell';
import {getTypedFieldValidationFunction} from 'cl-form/validation/validators';
import {FormSelectOptions} from 'cl-form/utils/orderByUtils';
import * as classNames from 'classnames';
import memoize from 'memoize-one';
import {Map} from 'immutable';
import update from 'immutability-helper';

export function getStickyColumnsNames(columns) {
  return _.keys(_.pickBy(columns, ({sticky}) => sticky));
}

export function getColumnDisplayName(columnName: string, columnData: ColumnsInfo): string {
  return columnData && columnData.displayName ? columnData.displayName : columnName;
}

export function renderCellContent( // TODO consider refactoring into a components.
  rowData: RowDataType,
  columnName: string,
  {renderRowHoverActions, options, type, getHref, color, composite}: ColumnsInfo = {},
  {
    visibleOnRowHover,
    inlineButtonDottedLine,
    inlineButtonRoot,
    tableCellContent,
  }: Partial<ClassNameMap<'visibleOnRowHover' | 'tableCellContent' | keyof typeof hoverContainerStyles>> = {}
) {
  const cellData = rowData[columnName];
  if ((_.isNil(cellData) || cellData === '') && !composite) {
    return cellData;
  }
  let render;
  if (type === 'date') {
    render = extractDateTime(cellData);
  } else if (type === 'html') {
    render = <span className={tableCellContent} dangerouslySetInnerHTML={{__html: cellData as string}} />;
  } else if (composite) {
    render = _.join(
      _.filter(
        _.map(composite, (columnDisplayName, rowDataKey) => {
          const rowDataValue = _.get(rowData, rowDataKey);
          if (rowDataValue) {
            return `${columnDisplayName}: ${rowDataValue}`;
          }
          return null;
        })
      ),
      ' | '
    );
  } else {
    render = _.isEmpty(options) ? cellData : getCellWithOptionsDisplayData(options, cellData);
  }
  const href = getHref && getHref(rowData);
  if (href) {
    render = (
      <InlineButton
        style={color ? {color} : undefined}
        classes={{root: classNames(inlineButtonRoot, tableCellContent), dottedLine: inlineButtonDottedLine}}
        href={href}
      >
        {render}
      </InlineButton>
    );
  }
  if (renderRowHoverActions) {
    render = (
      <React.Fragment>
        {render}
        {renderRowHoverActions(rowData, visibleOnRowHover)}
      </React.Fragment>
    );
  }
  if (href || type === 'html') {
    return render;
  }
  return <span className={tableCellContent}>{render}</span>;
}

export function getCellWithOptionsDisplayData(options: FormSelectOptions, value: FilterValueType) {
  return _.get(_.find(options, ({value: v}) => v == value), 'label', value); // using == for cases where option value may change between string and number
}

export function getSelectionByIndices(indices, data, columnsConfig) {
  const ids = [];
  _.forEach(indices, index => {
    ids.push(columnsConfig.id ? data.get(index)[columnsConfig.id] : index);
  });
  return {include: ids};
}

export function getValidateValue(
  rowData,
  columnName,
  columnData: ColumnsInfo,
  data?: Map<number, RowDataType>
): EditableTableCellProps['validate'] {
  const validationFunc = getTypedFieldValidationFunction(columnName, columnData);
  return validationFunc
    ? ({value}) => {
        const error = validationFunc(value, rowData, data);
        if (error) {
          return {value: error};
        }
        return {};
      }
    : null;
}

export function getNoResultsFoundEmptyStateProps() {
  return {
    headline: 'No results found',
    paragraphs: ['To get results, try adjusting or removing filters'],
  };
}

export function sortColumnsWithChildren(columns) {
  const sortedColumnNames = _.keys(columns);
  sortedColumnNames.sort((aspectA, aspectB) => {
    const columnA = columns[aspectA];
    const columnB = columns[aspectB];
    if (columnA.children && _.includes(columnA.children, aspectB)) {
      return -1;
    } else if (columnB.children && _.includes(columnB.children, aspectA)) {
      return 1;
    } else {
      return 0;
    }
  });
  return sortedColumnNames;
}

export const sortTableRows = memoize((columns, data, sortProps) => {
  if (!sortProps) {
    return data;
  }
  const columnToSort = _.keys(sortProps)[0];
  const orderType = sortProps[columnToSort];
  const valueType = columns[columnToSort].type;
  return data.sort((a, b) => {
    let valueA = a[columnToSort];
    let valueB = b[columnToSort];
    valueA = isNumericType(valueType) ? _.toNumber(valueA) : valueA;
    valueB = isNumericType(valueType) ? _.toNumber(valueB) : valueB;
    let result = 0;
    if (_.isString(valueA) && _.isString(valueB)) {
      result = valueA.localeCompare(valueB);
    } else {
      result = valueA - valueB;
    }
    return orderType === 'asc' ? result : -1 * result;
  });
});

export const isNumericType = (type: string) => {
  return type === 'number' || type === 'long' || type === 'float' || type === 'integer';
};

export function isChildRow(rowData, parentIndexName = ROW_PARENT_INDEX): boolean {
  return rowData && !_.isUndefined(rowData[parentIndexName]);
}

export function isParentRow(rowData): boolean {
  return rowData && !_.isUndefined(rowData[ROW_CHILDREN_INDICES]);
}

export const isParentDisabledByChild = (rowData: RowDataType, fieldName: string, data: Map<number, RowDataType>) => {
  let disabled = false;
  _.forEach(getChildrenRows(rowData, data), child => {
    //for child specifics
    if (child && child[fieldName]) {
      disabled = true;
      return false; //break forEach
    }
  });
  return disabled;
};

export const isChildDisabledByParent = (rowData: RowDataType, fieldName: string, data: Map<number, RowDataType>) => {
  const parent = getParentRow(rowData, data);
  return !!(parent && parent[fieldName]);
};

export const getChildrenIndices = rowData => {
  return rowData[ROW_CHILDREN_INDICES];
};

export const getChildrenRows = (rowData, data) => {
  return _.map(getChildrenIndices(rowData), childIndex => data.get(childIndex));
};

export const getParentIndex = rowData => {
  return rowData[ROW_PARENT_INDEX];
};

export const getParentRow = (rowData, data) => {
  return data.get(getParentIndex(rowData));
};

export const getSiblings = (rowData: RowDataType, data: Map<number, any>) => {
  return _.map(
    getParentRow(rowData, data)[ROW_CHILDREN_INDICES],
    (siblingIndex: number) => rowData[ROW_INDEX] !== siblingIndex && data && data.get(siblingIndex)
  );
};

export const updateDataIfHasChildren = _.memoize(
  (data: Map<number, RowDataType>, columnsConfig: ColumnsConfig<RowDataType>, supportChildren: boolean) => {
    if (supportChildren && columnsConfig) {
      const {id, parentId} = columnsConfig;
      const rowChildren = {};
      data.entrySeq().forEach(([rowIndex, rowData]) => {
        if (isChildRow(rowData, parentId)) {
          rowChildren[rowData[parentId]] = [...(rowChildren[rowData[parentId]] || []), rowIndex];
        }
      });
      let updatedData = Map<number, RowDataType>();
      let index = 0;
      data.entrySeq().forEach(([rowIndex, rowData]) => {
        const rowId = rowData[id];
        if (!isChildRow(rowData, parentId)) {
          if (rowChildren[rowId]) {
            updatedData = updatedData.set(
              index++,
              update(rowData, {
                $merge: {
                  [ROW_CHILDREN_INDICES]: rowChildren[rowId],
                },
              })
            );
            _.forEach(rowChildren[rowId], childIndex => {
              updatedData = updatedData.set(
                index++,
                update(data.get(childIndex), {
                  $merge: {[ROW_INDEX]: childIndex, [ROW_PARENT_INDEX]: rowIndex},
                })
              );
            });
          } else {
            updatedData = updatedData.set(index++, rowData);
          }
        }
      });
      return updatedData;
    }
    return data;
  }
);

export const extractDateTime = dateTime =>
  new Date((dateTime as number) * 1000).toLocaleString(undefined, {hour12: false});
